import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: '1000px', mx: 'auto', pb: 6, px: { xs: 1, sm: 2 } }}>
      {/* Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/dashboard')}
          sx={{
            color: '#4CA1AF',
            textTransform: 'none',
            '&:hover': { background: 'rgba(76, 161, 175, 0.1)' },
          }}
        >
          Back to Dashboard
        </Button>

        <Button
          endIcon={<OpenInNewIcon />}
          component="a"
          href="https://glocalprayer.net/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#6DD5FA',
            textTransform: 'none',
            fontSize: '0.85rem',
            '&:hover': { background: 'rgba(109, 213, 250, 0.1)' },
          }}
        >
          View on glocalprayer.net
        </Button>
      </Box>

      {/* Header Paper */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.95) 0%, rgba(26, 31, 46, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(76, 161, 175, 0.3)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'rgba(76, 161, 175, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SecurityIcon sx={{ color: '#4CA1AF', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: { xs: '1.5rem', sm: '1.85rem' } }}>
              Privacy Policy
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Last updated: January 2026 • Glocal Prayer Network (GPN)
            </Typography>
          </Box>
        </Box>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.95rem', lineHeight: 1.6, mt: 1 }}>
          Glocal Prayer Network is deeply committed to protecting the privacy, dignity, and sacred confidentiality of our users and ministry administrators. This Privacy Policy details how we handle, protect, and respect your personal and prayer information.
        </Typography>
      </Paper>

      {/* Content Paper */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: 'rgba(30, 41, 59, 0.85)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          color: 'rgba(255, 255, 255, 0.85)',
          lineHeight: 1.7,
        }}
      >
        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          1. What Information Do We Process?
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          When you use or administer services on GPN, we process personal information depending on your role and features utilized:
        </Typography>
        <List sx={{ pl: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="Account Information: Name, email address, phone number, encrypted password, profile photo, and designated church or organization affiliation." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="Administrative Credentials: Organization role, administrative permissions, activity audit logs, and digital signature records." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="Prayer & Devotional Data: Prayer requests, prayer category tags, public/private visibility preferences, daily devotion completions, and testimonies." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="Community Coordination Data: Driver information for Ride Connect and item descriptions for Share & Accept." />
          </ListItem>
        </List>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          2. Protection of Sacred & Private Prayer Requests
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          We treat prayer requests with spiritual reverence. Requests marked as <strong>Private</strong> are encrypted and accessible exclusively to authorized pastoral staff and designated intercessors of your specific organization. Private prayers are never displayed on public feeds or shared with external parties.
        </Typography>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          3. How We Process and Use Your Information
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          We process information strictly to provide, improve, and administer our faith community services:
        </Typography>
        <List sx={{ pl: 2, listStyleType: 'disc' }}>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="To coordinate group prayer initiatives, Prayer Closet commitments, and church events." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="To deliver push notifications for prayer reminders, devotionals, and church announcements." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="To enable administrators to steward their church branch securely and support their congregation." />
          </ListItem>
          <ListItem sx={{ display: 'list-item', py: 0.5 }}>
            <ListItemText primary="We DO NOT sell, rent, or monetize your personal or prayer data to third-party advertisers." />
          </ListItem>
        </List>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          4. Data Security & Storage
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          We implement rigorous security controls, including industry-standard SSL/TLS encryption for all data in transit, secure database authentication, role-based access restrictions, and regular security patching to protect your information against unauthorized access, disclosure, or alteration.
        </Typography>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <Typography variant="h6" sx={{ color: '#4CA1AF', fontWeight: 700, mb: 1.5 }}>
          5. Your Privacy Rights & Data Subject Requests
        </Typography>
        <Typography paragraph sx={{ fontSize: '0.95rem' }}>
          You have the right to review, update, export, or delete your account information and prayer history at any time. To submit a data request or account deletion inquiry, contact our Data Protection Officer at{' '}
          <a href="mailto:privacy@glocalprayer.net" style={{ color: '#4CA1AF', textDecoration: 'underline' }}>
            privacy@glocalprayer.net
          </a>.
        </Typography>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicy;
